'use client';

import React, { useState, useEffect, useCallback } from 'react';
import { TrendingUp, TrendingDown, Minus, BarChart2, Zap, Brain, Shuffle, Activity,  } from 'lucide-react';
import { Line, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer, AreaChart, Area,  } from 'recharts';
import { getBrainCheckHistory } from '@/lib/services/brainCheckService';

import { useAuth } from '@/contexts/AuthContext';

// ─── Types ────────────────────────────────────────────────────────────────────

interface SessionComparison {
  domain: string;
  icon: React.ReactNode;
  color: string;
  current: number;
  previous: number;
  delta: number;
  trend: 'improved' | 'stable' | 'declined';
  message: string;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function computeTrend(delta: number): 'improved' | 'stable' | 'declined' {
  if (delta > 3) return 'improved';
  if (delta < -3) return 'declined';
  return 'stable';
}

function buildTrendMessage(domain: string, trend: 'improved' | 'stable' | 'declined', delta: number): string {
  const abs = Math.abs(Math.round(delta));
  if (trend === 'improved') return `${domain} improved by ${abs} points after your last session`;
  if (trend === 'declined') return `${domain} was ${abs} points lower — rest supports recovery`;
  return `${domain} is holding steady — consistent baseline`;
}

// ─── Trend badge ──────────────────────────────────────────────────────────────

function TrendBadge({ trend, delta }: { trend: 'improved' | 'stable' | 'declined'; delta: number }) {
  const abs = Math.abs(Math.round(delta));
  const config = {
    improved: { icon: <TrendingUp size={11} />, color: '#10B981', bg: 'rgba(16,185,129,0.12)', text: `+${abs}` },
    stable:   { icon: <Minus size={11} />,       color: '#38BDF8', bg: 'rgba(56,189,248,0.12)',  text: `±${abs}` },
    declined: { icon: <TrendingDown size={11} />, color: '#F97316', bg: 'rgba(249,115,22,0.12)', text: `-${abs}` },
  }[trend];

  return (
    <div className="flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold"
      style={{ color: config.color, background: config.bg }}>
      {config.icon}
      {config.text}
    </div>
  );
}

// ─── Domain comparison row ────────────────────────────────────────────────────

function DomainRow({ comparison }: { comparison: SessionComparison }) {
  const barWidth = Math.min(comparison.current, 100);
  const prevBarWidth = Math.min(comparison.previous, 100);

  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 rounded-lg flex items-center justify-center flex-shrink-0"
            style={{ background: `${comparison.color}15`, color: comparison.color }}>
            {comparison.icon}
          </div>
          <span className="text-[12px] font-semibold text-white">{comparison.domain}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[12px] font-bold text-white">{Math.round(comparison.current)}</span>
          <TrendBadge trend={comparison.trend} delta={comparison.delta} />
        </div>
      </div>

      {/* Dual bar: current vs previous */}
      <div className="space-y-1">
        <div className="flex items-center gap-2">
          <span className="text-[9px] text-zinc-600 w-12 flex-shrink-0">Now</span>
          <div className="flex-1 h-2 bg-[#1E3A5F]/30 rounded-full overflow-hidden">
            <div className="h-full rounded-full transition-all duration-700"
              style={{ width: `${barWidth}%`, background: comparison.color }} />
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[9px] text-zinc-600 w-12 flex-shrink-0">Before</span>
          <div className="flex-1 h-1.5 bg-[#1E3A5F]/20 rounded-full overflow-hidden">
            <div className="h-full rounded-full transition-all duration-700"
              style={{ width: `${prevBarWidth}%`, background: `${comparison.color}60` }} />
          </div>
        </div>
      </div>

      <p className="text-[10px] text-zinc-500 leading-relaxed pl-8">{comparison.message}</p>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

interface SessionProgressComparisonProps {
  compact?: boolean;
}

export default function SessionProgressComparison({ compact = false }: SessionProgressComparisonProps) {
  const { user } = useAuth();
  const [comparisons, setComparisons] = useState<SessionComparison[]>([]);
  const [chartData, setChartData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [overallTrend, setOverallTrend] = useState<'improved' | 'stable' | 'declined'>('stable');
  const [overallMessage, setOverallMessage] = useState('');
  const [sessionCount, setSessionCount] = useState(0);

  const loadData = useCallback(async () => {
    if (!user?.id) return;
    setLoading(true);
    try {
      const history = await getBrainCheckHistory(user.id, 10);
      if (history.length < 2) {
        setLoading(false);
        return;
      }

      const latest = history[0];
      const previous = history[1];
      setSessionCount(history.length);

      const domains: SessionComparison[] = [
        {
          domain: 'Focus',
          icon: <Zap size={12} />,
          color: '#F97316',
          current: latest.focus_score ?? 0,
          previous: previous.focus_score ?? 0,
          delta: (latest.focus_score ?? 0) - (previous.focus_score ?? 0),
          trend: computeTrend((latest.focus_score ?? 0) - (previous.focus_score ?? 0)),
          message: '',
        },
        {
          domain: 'Memory',
          icon: <Brain size={12} />,
          color: '#8B5CF6',
          current: latest.mental_energy_score ?? 0,
          previous: previous.mental_energy_score ?? 0,
          delta: (latest.mental_energy_score ?? 0) - (previous.mental_energy_score ?? 0),
          trend: computeTrend((latest.mental_energy_score ?? 0) - (previous.mental_energy_score ?? 0)),
          message: '',
        },
        {
          domain: 'Stability',
          icon: <Activity size={12} />,
          color: '#10B981',
          current: latest.stability_score ?? 0,
          previous: previous.stability_score ?? 0,
          delta: (latest.stability_score ?? 0) - (previous.stability_score ?? 0),
          trend: computeTrend((latest.stability_score ?? 0) - (previous.stability_score ?? 0)),
          message: '',
        },
        {
          domain: 'Flexibility',
          icon: <Shuffle size={12} />,
          color: '#38BDF8',
          current: latest.flexibility_score ?? 0,
          previous: previous.flexibility_score ?? 0,
          delta: (latest.flexibility_score ?? 0) - (previous.flexibility_score ?? 0),
          trend: computeTrend((latest.flexibility_score ?? 0) - (previous.flexibility_score ?? 0)),
          message: '',
        },
      ].map(d => ({ ...d, message: buildTrendMessage(d.domain, d.trend, d.delta) }));

      setComparisons(domains);

      // Overall trend
      const overallDelta = (latest.overall_score ?? 0) - (previous.overall_score ?? 0);
      const trend = computeTrend(overallDelta);
      setOverallTrend(trend);
      setOverallMessage(
        trend === 'improved'
          ? `Your brain state improved after your last session — keep the momentum going.`
          : trend === 'declined'
          ? `Your state was slightly lower this session — rest and recovery are part of the process.`
          : `Your brain state is consistent — a stable baseline is a strong foundation.`
      );

      // Chart data
      const chart = history.slice(0, 7).reverse().map((s: any, i: number) => ({
        session: `S${i + 1}`,
        Focus: Math.round(s.focus_score ?? 0),
        Memory: Math.round(s.mental_energy_score ?? 0),
        Overall: Math.round(s.overall_score ?? 0),
      }));
      setChartData(chart);
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => { loadData(); }, [loadData]);

  if (loading) {
    return (
      <div className="rounded-2xl border border-[#1E3A5F]/40 bg-[#0B1628]/60 p-4">
        <div className="space-y-3">
          <div className="h-3 bg-[#1E3A5F]/40 rounded-full animate-pulse w-1/2" />
          <div className="h-2 bg-[#1E3A5F]/30 rounded-full animate-pulse w-full" />
          <div className="h-2 bg-[#1E3A5F]/30 rounded-full animate-pulse w-3/4" />
        </div>
      </div>
    );
  }

  if (comparisons.length === 0) {
    return (
      <div className="rounded-2xl border border-[#1E3A5F]/40 bg-[#0B1628]/60 p-4 text-center">
        <BarChart2 size={24} className="text-[#4A7AB5] mx-auto mb-2" />
        <p className="text-[12px] text-zinc-400">Complete 2 or more brain checks to see your progress comparison.</p>
      </div>
    );
  }

  const trendConfig = {
    improved: { color: '#10B981', icon: <TrendingUp size={14} />, bg: 'rgba(16,185,129,0.10)', border: 'rgba(16,185,129,0.25)' },
    stable:   { color: '#38BDF8', icon: <Minus size={14} />,       bg: 'rgba(56,189,248,0.10)',  border: 'rgba(56,189,248,0.25)' },
    declined: { color: '#F97316', icon: <TrendingDown size={14} />, bg: 'rgba(249,115,22,0.10)', border: 'rgba(249,115,22,0.25)' },
  }[overallTrend];

  return (
    <div className="space-y-4">
      {/* Overall trend banner */}
      <div className="rounded-2xl p-4 border flex items-start gap-3"
        style={{ background: trendConfig.bg, borderColor: trendConfig.border }}>
        <div className="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0"
          style={{ background: `${trendConfig.color}20`, color: trendConfig.color }}>
          {trendConfig.icon}
        </div>
        <div>
          <p className="text-[12px] font-semibold text-white leading-snug">{overallMessage}</p>
          <p className="text-[10px] text-zinc-500 mt-1">{sessionCount} sessions tracked</p>
        </div>
      </div>

      {/* Domain comparisons */}
      {!compact && (
        <div className="rounded-2xl border border-[#1E3A5F]/40 bg-[#0B1628]/60 p-4 space-y-4">
          <p className="text-[11px] text-[#8BAFD4] uppercase tracking-wider font-medium">Session-to-session comparison</p>
          {comparisons.map((c, i) => (
            <React.Fragment key={c.domain}>
              {i > 0 && <div className="border-t border-[#1E3A5F]/30" />}
              <DomainRow comparison={c} />
            </React.Fragment>
          ))}
        </div>
      )}

      {/* Trend chart */}
      {chartData.length > 2 && !compact && (
        <div className="rounded-2xl border border-[#1E3A5F]/40 bg-[#0B1628]/60 p-4">
          <p className="text-[11px] text-[#8BAFD4] uppercase tracking-wider font-medium mb-3">Recent trend</p>
          <div className="h-36">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData}>
                <defs>
                  <linearGradient id="overallGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#F97316" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#F97316" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#1E3A5F" />
                <XAxis dataKey="session" tick={{ fill: '#8BAFD4', fontSize: 9 }} />
                <YAxis domain={[0, 100]} tick={{ fill: '#8BAFD4', fontSize: 9 }} />
                <Tooltip contentStyle={{ background: '#0A1628', border: '1px solid #1E3A5F', borderRadius: 8, fontSize: 11 }} />
                <Area type="monotone" dataKey="Overall" stroke="#F97316" fill="url(#overallGrad)" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="Focus" stroke="#10B981" strokeWidth={1.5} dot={false} strokeDasharray="3 3" />
                <Line type="monotone" dataKey="Memory" stroke="#8B5CF6" strokeWidth={1.5} dot={false} strokeDasharray="3 3" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Compact domain grid */}
      {compact && (
        <div className="grid grid-cols-2 gap-2">
          {comparisons.map(c => (
            <div key={c.domain} className="rounded-xl border border-[#1E3A5F]/40 bg-[#0B1628]/60 p-3">
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-1.5" style={{ color: c.color }}>
                  {c.icon}
                  <span className="text-[11px] font-semibold">{c.domain}</span>
                </div>
                <TrendBadge trend={c.trend} delta={c.delta} />
              </div>
              <p className="text-[16px] font-bold text-white">{Math.round(c.current)}</p>
              <p className="text-[9px] text-zinc-600">prev: {Math.round(c.previous)}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
